import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Select,
  Form,
  Input,
  Button,
  Table,
  Modal,
  Typography,
  DatePicker,
  TimePicker,
  Space,
  message,
  Tag,
  Divider
} from 'antd';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import {
  apiGetLinkDown,
  apiGetDevicesInformation,
  apiCreateLinkDown,
  apiGetUserInfo,
  apiGetDevicesInformationV2,
  apiDeleteLinkDown
} from '@services/ManageDevices/DeviceServices';
import {
  SendOutlined,
  UploadOutlined,
  DownloadOutlined,
  SearchOutlined,
  SyncOutlined,
  MinusCircleOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { showError, showSuccess, showWarning } from '@components/notification';
import { convertLocaltimeToUTC, downloadBlobFile, formatCurrency } from '@utils/utils';
import { URL_API } from '@configs/index';
import ModalImportFile from './ModalImportFile';
import DeviceSelectV2 from '@components/Select/SelectDeviceV2';
import { usePermission } from '@contexts/Auth/UsePermission';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const optionTime = [
  { value: '5', label: '5 minutes' },
  { value: '10', label: '10 minutes' },
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' },
  { value: '720', label: '12 hours' },
  { value: '1440', label: '1 day' }
];

export default function SendDownLinkCommand() {
  const [deviceList, setDeviceList] = useState([]);
  const [historicalCmd, setHistoricalCmd] = useState([]);
  const [cmd, setCmd] = useState({});
  const [notification, setNotification] = useState('');
  const [user, setUser] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [freezeDate, setFreezeDate] = useState(null);
  const [visibleModalImport, setVisibleModalImport] = useState(false);
  const [inputError, setInputError] = useState('');
  const [flagSearch, setFlagSearch] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const [search, setSearch] = useState('');
  const { command } = usePermission();

  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };

  const query = useQuery();
  const role = query.get('role');
  const serial_no = query.get('SN');

  const isAdmin = true;

  const onChangeDateRange = (dates, dateStrings) => {
    setDateRange(dates);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const fetchSendLinkDown = async () => {
    try {
      const res = await apiGetLinkDown({
        page: currentPage,
        size: pageSize,
        keyWord: search || undefined,
        fromDate: dateRange?.length
          ? convertLocaltimeToUTC(dateRange?.[0], '00:00:00.000')
          : undefined,
        toDate: dateRange?.length
          ? convertLocaltimeToUTC(dateRange?.[1], '23:59:59.999')
          : undefined
      });
      setHistoricalCmd(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + index + 1
        }))
      );
      setTotalItems(res.data?.meta?.total);
    } catch (err) {
      console.log(err);
    }
  };

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const onChangeFreezeDate = (date, dateStr) => {
    setCmd((state) => ({
      ...state,
      readDayFrozenData: { readDayFrozenData: Number(moment(dateStr)?.format('YYMMDD')) }
    }));
    setFreezeDate(date);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Xác nhận xóa',
      content: `Bạn có chắc chắn muốn xóa lệnh này không?`,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await apiDeleteLinkDown(record.id);
          showSuccess('Xóa lệnh thành công');
          fetchSendLinkDown();
        } catch (error) {
          console.error('Error deleting command:', error);
          showError('Xóa lệnh thất bại');
        }
      }
    });
  };

  const columns = useMemo(
    () =>
      [
        {
          title: 'STT',
          dataIndex: 'stt',
          width: 10
        },
        {
          title: 'Trạng thái',
          dataIndex: 'is_sent',
          render: (text, record) => {
            const now = new Date();
            const sentAt = new Date(record.sent_at);
            const isExpired = now.getTime() - 60000 > sentAt.getTime();

            if (isExpired && record.is_sent !== 2 && record.sent_at) {
              return (
                <Tag icon={<MinusCircleOutlined />} color="red">
                  Gửi thất bại
                </Tag>
              );
            } else if (record.is_sent === 1) {
              return (
                <Tag icon={<SyncOutlined spin />} color="processing">
                  Đang gửi
                </Tag>
              );
            } else if (record.is_sent === 2) {
              return (
                <Tag icon={<CheckCircleOutlined />} color="success">
                  Đã gửi
                </Tag>
              );
            } else {
              return (
                <Tag icon={<CloseCircleOutlined />} color="error">
                  Chưa gửi
                </Tag>
              );
            }
          },
          width: 130
        },
        {
          title: 'Số Serial',
          dataIndex: 'serial_no',
          width: 10
        },
        {
          title: 'imei',
          dataIndex: 'imei',
          width: 10
        },
        {
          title: 'Model',
          dataIndex: 'model',
          width: 10
        },
        {
          title: 'Gửi lúc',
          dataIndex: 'sent_at',
          render: (text, record) => {
            return (
              <div>
                {record.sent_at ? moment(record.sent_at).format('DD/MM/YYYY HH:mm:ss') : 'unknown'}
              </div>
            );
          },
          width: 10
        },
        {
          title: 'Trả về lúc',
          dataIndex: 'return_at',
          render: (text, record) => {
            return (
              <div>
                {record.return_at
                  ? moment(record.return_at).format('DD/MM/YYYY HH:mm:ss')
                  : 'unknown'}
              </div>
            );
          },
          width: 10
        },
        {
          title: 'Tạo lúc',
          dataIndex: 'created_at',
          render: (text, record) => {
            return <div>{moment(record.created_at).format('DD/MM/YYYY HH:mm')}</div>;
          },
          width: 10
        },
        {
          title: 'Người tạo',
          dataIndex: 'created_by',
          width: 10
        },
        {
          title: 'Lệnh',
          dataIndex: 'cmd',
          width: 20
        },
        {
          title: 'Thao tác',
          dataIndex: 'action',
          render: (text, record) => {
            // Chỉ hiển thị nút xóa nếu lệnh chưa gửi (is_sent === 0)
            if (record.is_sent === 0) {
              return (
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record)}>
                  Xóa
                </Button>
              );
            }
            return null;
          },
          width: 80,
          align: 'center'
        },
        {
          title: 'Lệnh trả về',
          dataIndex: 'return_cmd',
          render: (text, record) => {
            return (
              <>
                {command?.viewResponse && (
                  <Button
                    type="link"
                    onClick={() => {
                      setNotification(record.return_cmd || 'No return command available');
                      setOpen(true);
                    }}>
                    {record?.return_cmd ? 'Xem' : ''}
                  </Button>
                )}
              </>
            );
          },
          width: 60
        }
      ].filter((col) => {
        // Ẩn cột "Lệnh trả về" nếu không có quyền viewResponse
        if (col.title === 'Lệnh trả về' && !command?.viewResponse) {
          return false;
        }
        return true;
      }),
    [deviceList, command?.viewResponse]
  );

  useEffect(() => {
    fetchSendLinkDown();
  }, [currentPage, pageSize, selectedDevice?.value, flagSearch, dateRange]);

  // Set search keyword when device is selected
  useEffect(() => {
    if (selectedDevice) {
      setSearch(selectedDevice?.serial_no || selectedDevice?.imei || '');
    } else {
      setSearch('');
    }
  }, [selectedDevice?.value]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== undefined) {
        setCurrentPage(1);
        setFlagSearch((prev) => !prev);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchSendLinkDown();
    }, 10000);

    return () => clearInterval(interval);
  }, [search, currentPage, pageSize, dateRange]);

  useEffect(() => {
    setSelectedDevice(deviceList?.find((item) => item?.serial_no === serial_no));
  }, [serial_no, deviceList?.length]);

  const handleSendCmd = async (type) => {
    if (!selectedDevice?.imei) {
      showWarning({ message: 'Bạn chưa chọn thiết bị!' });
      return;
    }
    try {
      if (!cmd[type]) {
        showWarning({ message: 'Nhập giá trị lệnh!' });
        return;
      }
      if (type === 'reportPeriod' && Number(cmd[type]?.setReportPeriod) < 60) {
        message.error('Chu kỳ gửi báo cáo phải lớn hơn hoặc bằng 60 phút');
        return;
      }
      const payload = {
        imei: selectedDevice?.imei,
        serial_no: selectedDevice?.serial_no,
        cmd: cmd[type]
      };
      const res = await apiCreateLinkDown(payload);
      if (res?.data.status === 200) {
        showSuccess({ message: 'Dữ liệu cài đặt đã được lưu lại trong bộ nhớ!' });
        fetchSendLinkDown();
      } else {
        showError('Có lỗi xảy ra!');
      }
    } catch (err) {
      console.log(err);
      // showError('Có lỗi xảy ra!' + err);
    }
  };

  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleShowError = () => {
    showWarning({ message: 'Bạn không có quyền thực hiện chức năng này!' });
  };

  const [loading, setLoading] = useState(false);
  const handleExportExcelButtonClick = async () => {
    try {
      if (!search && (!dateRange?.[0] || !dateRange?.[1])) {
        showWarning({
          message:
            'Vì dữ liệu quá lớn, Vui lòng chọn ngày hoặc nhập từ khóa tìm kiếm để xuất dữ liệu!'
        });
        return;
      }
      setLoading(true);
      await downloadBlobFile(
        `${URL_API}/api/down-link-command/export-report`,
        'Danh sách lệnh truyền.xlsx',
        {
          keyWord: search || undefined,
          fromDate: dateRange?.length
            ? convertLocaltimeToUTC(dateRange?.[0], '00:00:00.000')
            : undefined,
          toDate: dateRange?.length
            ? convertLocaltimeToUTC(dateRange?.[1], '23:59:59.999')
            : undefined
        },
        'GET'
      );
    } catch (e) {
      showError('Có lỗi xảy ra, vui lòng liên hệ ban quản trị');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Modal
        title="Thông tin lệnh trả về"
        visible={open}
        onOk={handleClose}
        onCancel={handleClose}
        footer={[
          <Button key="close" onClick={handleClose}>
            Đóng
          </Button>
        ]}>
        <p>{notification}</p>
      </Modal>

      <ModalImportFile
        visible={visibleModalImport}
        onCancel={() => {
          setVisibleModalImport(false);
          fetchSendLinkDown();
        }}
      />
      <Row gutter={[16, 16]}>
        <Col md={24} xs={24}>
          <Card
            title="Thiết bị"
            headStyle={{ backgroundColor: '#0051A9', color: '#FFF', minHeight: '36px' }}>
            <Form.Item>
              <DeviceSelectV2
                isGetAllDevice={true}
                placeholder="Chọn thiết bị"
                onChange={(option) => {
                  setSelectedDevice(option);
                }}
              />
            </Form.Item>
            {selectedDevice && (
              <>
                <Typography.Title style={{ fontWeight: '400', fontSize: '14px' }}>
                  Số Serial: {selectedDevice?.serial_no}
                </Typography.Title>
                <Typography.Title style={{ fontWeight: '400', fontSize: '14px' }}>
                  Model: {selectedDevice?.model}
                </Typography.Title>
                <Typography.Title style={{ fontWeight: '400', fontSize: '14px' }}>
                  Chỉ số đồng hồ:{' '}
                  <strong>{formatCurrency(selectedDevice?.meter_reading / 1000, 3)} (m3)</strong>
                </Typography.Title>
              </>
            )}
          </Card>
        </Col>
        {command?.setBasic && (
          <Col md={12} xs={24}>
            <Card
              title="Chỉ số đồng hồ (L)"
              headStyle={{ backgroundColor: '#0051A9', color: '#FFF', minHeight: '36px' }}>
              <Form.Item validateStatus={inputError ? 'error' : ''} help={inputError || ''}>
                <Input
                  ref={inputRef}
                  placeholder={'Nhập chỉ số đồng hồ'}
                  type="number"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value && !Number.isInteger(Number(value))) {
                      setInputError('Vui lòng chỉ nhập vào số nguyên!');
                    } else {
                      setInputError('');
                    }

                    setCmd((state) => ({
                      ...state,
                      meterReading: { setMeterReading: e.target.value }
                    }));
                  }}
                />
              </Form.Item>
              <Button
                onClick={() => handleSendCmd('meterReading')}
                disabled={inputError}
                icon={<SendOutlined />}
                style={{
                  backgroundColor: '#0051A9',
                  borderColor: '#0051A9',
                  color: '#FFF',
                  height: '36px'
                }}>
                Gửi lệnh
              </Button>
            </Card>
          </Col>
        )}
        {command?.setAdvanced && (
          <>
            <Col md={12} xs={24}>
              <Card
                title="Thời điểm gửi báo cáo"
                headStyle={{ backgroundColor: '#0051A9', color: '#FFF', minHeight: '36px' }}>
                <Form.Item>
                  <TimePicker
                    style={{ width: '100%', height: '36px' }}
                    onChange={(time, timeString) => {
                      const [hours, minutes, seconds] = timeString.split(':');
                      const hour = parseInt(hours, 10);
                      const minute = parseInt(minutes, 10);
                      const second = parseInt(seconds, 10);
                      const reportingTime = hour * 3600 + minute * 60 + second;
                      setCmd((state) => ({
                        ...state,
                        reportingTime: { setReportingTime: reportingTime }
                      }));
                    }}
                    changeOnScroll
                    needConfirm={false}
                    disabledHours={() => {
                      const hours = Array.from({ length: 24 }, (_, i) => i);
                      // Disable hours 22, 23
                      return hours.filter((hour) => hour >= 22);
                    }}
                    disabledMinutes={(selectedHour) => {
                      if (selectedHour >= 22) {
                        return Array.from({ length: 60 }, (_, i) => i); // Disable all minutes for 22:00-23:59
                      }
                      return []; // Allow all minutes for 00:00-21:59
                    }}
                  />
                </Form.Item>
                <Button
                  onClick={() => handleSendCmd('reportingTime')}
                  icon={<SendOutlined />}
                  style={{
                    backgroundColor: '#0051A9',
                    borderColor: '#0051A9',
                    color: '#FFF',
                    height: '36px'
                  }}>
                  Gửi lệnh
                </Button>
              </Card>
            </Col>
            <Col md={12} xs={24}>
              <Card
                title="Chu kỳ gửi báo cáo (phút)"
                headStyle={{ backgroundColor: '#0051A9', color: '#FFF', minHeight: '36px' }}>
                <Form.Item
                  name="reportPeriod"
                  validateTrigger={['onBlur', 'onChange']}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value && value < 60) {
                          return Promise.reject(
                            new Error('Chu kỳ gửi báo cáo phải lớn hơn hoặc bằng 60 phút')
                          );
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}>
                  <Input
                    placeholder="Nhập Chu kỳ gửi báo phải lớn hơn hoặc bằng 60 phút"
                    type="number"
                    min={60}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCmd((state) => ({
                        ...state,
                        reportPeriod: { setReportPeriod: value }
                      }));
                    }}
                  />
                </Form.Item>
                <Button
                  onClick={() => handleSendCmd('reportPeriod')}
                  icon={<SendOutlined />}
                  style={{
                    backgroundColor: '#0051A9',
                    borderColor: '#0051A9',
                    color: '#FFF',
                    height: '36px'
                  }}>
                  Gửi lệnh
                </Button>
              </Card>
            </Col>
            <Col md={12} xs={24}>
              <Card
                title="Đọc lại dữ liệu dày đặc"
                headStyle={{ backgroundColor: '#0051A9', color: '#FFF', minHeight: '36px' }}>
                <Form.Item>
                  <DatePicker
                    value={freezeDate}
                    style={{ width: '100%', height: '36px' }}
                    format="YYYY-MM-DD"
                    onChange={onChangeFreezeDate}
                    placeholder="Ngày đọc lại"
                  />
                </Form.Item>
                <Button
                  onClick={() => handleSendCmd('readDayFrozenData')}
                  icon={<SendOutlined />}
                  style={{
                    backgroundColor: '#0051A9',
                    borderColor: '#0051A9',
                    color: '#FFF',
                    height: '36px'
                  }}>
                  Gửi lệnh
                </Button>
              </Card>
            </Col>
            <Col md={12} xs={24}>
              <Card
                title="Số lít/xung (L/P)"
                headStyle={{ backgroundColor: '#0051A9', color: '#FFF', minHeight: '36px' }}>
                <Form.Item>
                  <Select
                    style={{ width: '100%', height: '36px' }}
                    placeholder={'Select'}
                    onChange={(value) =>
                      setCmd((state) => ({
                        ...state,
                        pulseConstant: { setPulseConstant: value }
                      }))
                    }
                    options={[
                      { value: '1', label: '1Lít/Xung' },
                      { value: '2', label: '10Lít/Xung' },
                      { value: '3', label: '100Lít/Xung' },
                      { value: '4', label: '1000Lít/Xung' },
                      { value: '5', label: '0.5Lít/Xung' },
                      { value: '6', label: '5Lít/Xung' },
                      { value: '9', label: '3Lít/Xung' },
                      { value: '10', label: '30Lít/Xung' }
                    ]}
                  />
                </Form.Item>
                <Button
                  onClick={() => handleSendCmd('pulseConstant')}
                  icon={<SendOutlined />}
                  style={{
                    backgroundColor: '#0051A9',
                    borderColor: '#0051A9',
                    color: '#FFF',
                    height: '36px'
                  }}>
                  Gửi lệnh
                </Button>
              </Card>
            </Col>
            <Col md={12} xs={24}>
              <Card
                title="Đơn vị chia nhỏ nhất"
                headStyle={{ backgroundColor: '#0051A9', color: '#FFF', minHeight: '36px' }}>
                <Form.Item>
                  <Select
                    style={{ width: '100%', height: '36px' }}
                    placeholder={'Select'}
                    onChange={(value) =>
                      setCmd((state) => ({
                        ...state,
                        setSmallestScaleUnit: { setSmallestScaleUnit: value }
                      }))
                    }
                    options={[
                      { value: '5', label: '0.1L' },
                      { value: '1', label: '1L' },
                      { value: '2', label: '10L' },
                      { value: '3', label: '100L' },
                      { value: '4', label: '1000L' }
                    ]}
                  />
                </Form.Item>
                <Button
                  onClick={() => handleSendCmd('setSmallestScaleUnit')}
                  icon={<SendOutlined />}
                  style={{
                    backgroundColor: '#0051A9',
                    borderColor: '#0051A9',
                    color: '#FFF',
                    height: '36px'
                  }}>
                  Gửi lệnh
                </Button>
              </Card>
            </Col>
            {isAdmin && (
              <>
                <Col md={12} xs={24}>
                  <Card
                    title="Enable active alarm reporting flag"
                    headStyle={{ backgroundColor: '#0051A9', color: '#FFF', minHeight: '36px' }}>
                    <Form.Item>
                      <Select
                        style={{ width: '100%', height: '36px' }}
                        placeholder={'Select'}
                        onChange={(value) =>
                          setCmd((state) => ({
                            ...state,
                            setEnableActiveAlarmReportingFlag: {
                              setEnableActiveAlarmReportingFlag: value
                            }
                          }))
                        }
                        options={[
                          { value: '0', label: 'Turn off' },
                          { value: '1', label: 'Enable active reporting' },
                          { value: '2', label: 'First time proactive reporting' }
                        ]}
                      />
                    </Form.Item>
                    <Button
                      onClick={() => handleSendCmd('setEnableActiveAlarmReportingFlag')}
                      icon={<SendOutlined />}
                      style={{
                        backgroundColor: '#0051A9',
                        borderColor: '#0051A9',
                        color: '#FFF',
                        height: '36px'
                      }}>
                      Gửi lệnh
                    </Button>
                  </Card>
                </Col>
                <Col md={12} xs={24}>
                  <Card
                    title="Chu kỳ truyền lại (giây)"
                    headStyle={{ backgroundColor: '#0051A9', color: '#FFF', minHeight: '36px' }}>
                    <Form.Item>
                      <Input
                        placeholder={'Nhập Chu kỳ truyền lại (giây)'}
                        type="number"
                        onChange={(e) =>
                          setCmd((state) => ({
                            ...state,
                            retransmissionPeriod: { setRetransmissionPeriod: e.target.value }
                          }))
                        }
                      />
                    </Form.Item>
                    <Button
                      onClick={() => handleSendCmd('retransmissionPeriod')}
                      icon={<SendOutlined />}
                      style={{
                        backgroundColor: '#0051A9',
                        borderColor: '#0051A9',
                        color: '#FFF',
                        height: '36px'
                      }}>
                      Gửi lệnh
                    </Button>
                  </Card>
                </Col>
                <Col md={12} xs={24}>
                  <Card
                    title="Số lần truyền lại (lần)"
                    headStyle={{ backgroundColor: '#0051A9', color: '#FFF', minHeight: '36px' }}>
                    <Form.Item>
                      <Select
                        style={{ width: '100%', height: '36px' }}
                        placeholder={'nhập số lần truyền lại (lần)'}
                        onChange={(value) =>
                          setCmd((state) => ({
                            ...state,
                            retransmissionTimes: { setRetransmissionTimes: value }
                          }))
                        }
                        options={[
                          { label: '0', value: '0' },
                          { label: '1', value: '1' },
                          { label: '2', value: '2' },
                          { label: '3', value: '3' },
                          { label: '4', value: '4' },
                          { label: '5', value: '5' }
                        ]}
                      />
                    </Form.Item>
                    <Button
                      onClick={
                        isAdmin ? () => handleSendCmd('retransmissionTimes') : handleShowError
                      }
                      icon={<SendOutlined />}
                      style={{
                        backgroundColor: '#0051A9',
                        borderColor: '#0051A9',
                        color: '#FFF',
                        height: '36px'
                      }}>
                      Gửi lệnh
                    </Button>
                  </Card>
                </Col>

                <Col md={12} xs={24}>
                  <Card
                    title="Địa chỉ server"
                    headStyle={{ backgroundColor: '#0051A9', color: '#FFF', minHeight: '36px' }}>
                    <Form.Item>
                      <Input
                        type="text"
                        placeholder="Nhập địa chỉ server"
                        onChange={(e) =>
                          setCmd((state) => ({
                            ...state,
                            serverAddress: { setServerAddress: e.target.value }
                          }))
                        }
                      />
                    </Form.Item>
                    <Button
                      onClick={isAdmin ? () => handleSendCmd('serverAddress') : handleShowError}
                      icon={<SendOutlined />}
                      style={{
                        backgroundColor: '#0051A9',
                        borderColor: '#0051A9',
                        color: '#FFF',
                        height: '36px'
                      }}>
                      Gửi lệnh
                    </Button>
                  </Card>
                </Col>
                <Col md={12} xs={24}>
                  <Card
                    title="Chu kỳ ghi dữ liệu dày"
                    headStyle={{ backgroundColor: '#0051A9', color: '#FFF', minHeight: '36px' }}>
                    <Form.Item>
                      <Select
                        style={{ width: '100%', height: '36px' }}
                        placeholder={'Chọn'}
                        onChange={(value) =>
                          setCmd((state) => ({
                            ...state,
                            denseDataCycle: { setDenseDataCycle: value }
                          }))
                        }
                        options={optionTime}
                      />
                    </Form.Item>
                    <Button
                      onClick={isAdmin ? () => handleSendCmd('denseDataCycle') : handleShowError}
                      icon={<SendOutlined />}
                      style={{
                        backgroundColor: '#0051A9',
                        borderColor: '#0051A9',
                        color: '#FFF',
                        height: '36px'
                      }}>
                      Gửi lệnh
                    </Button>
                  </Card>
                </Col>
                <Col md={12} xs={24}>
                  <Card
                    title="Điều khiển valve"
                    headStyle={{ backgroundColor: '#0051A9', color: '#FFF', minHeight: '36px' }}>
                    <Form.Item>
                      <Select
                        style={{ width: '100%', height: '36px' }}
                        placeholder={'Chọn'}
                        onChange={(value) =>
                          setCmd((state) => ({
                            ...state,
                            valveControl: { setValveControl: value }
                          }))
                        }
                        options={[
                          { value: '0', label: 'Open' },
                          { value: '1', label: 'Close' }
                        ]}
                      />
                    </Form.Item>
                    <Button
                      onClick={isAdmin ? () => handleSendCmd('valveControl') : handleShowError}
                      icon={<SendOutlined />}
                      style={{
                        backgroundColor: '#0051A9',
                        borderColor: '#0051A9',
                        color: '#FFF',
                        height: '36px'
                      }}>
                      Gửi lệnh
                    </Button>
                  </Card>
                </Col>

                <Col md={12} xs={24}>
                  <Card
                    title="Múi giờ"
                    headStyle={{ backgroundColor: '#0051A9', color: '#FFF', minHeight: '36px' }}>
                    <Form.Item>
                      <Select
                        style={{ width: '100%', height: '36px' }}
                        placeholder={'Chọn'}
                        onChange={(value) =>
                          setCmd((state) => ({
                            ...state,
                            timeZone: { setTimeZone: value }
                          }))
                        }
                        options={[
                          { label: 'UTC+8', value: 'UTC+8' },
                          { label: 'UTC+7', value: 'UTC+7' },
                          { label: 'UTC+6', value: 'UTC+6' },
                          { label: 'UTC+5', value: 'UTC+5' },
                          { label: 'UTC+4', value: 'UTC+4' },
                          { label: 'UTC+3', value: 'UTC+3' },
                          { label: 'UTC+2', value: 'UTC+2' },
                          { label: 'UTC+1', value: 'UTC+1' }
                        ]}
                      />
                    </Form.Item>
                    <Button
                      onClick={isAdmin ? () => handleSendCmd('timeZone') : handleShowError}
                      icon={<SendOutlined />}
                      style={{
                        backgroundColor: '#0051A9',
                        borderColor: '#0051A9',
                        color: '#FFF',
                        height: '36px'
                      }}>
                      Gửi lệnh
                    </Button>
                  </Card>
                </Col>
                <Col md={12} xs={24}>
                  <Card
                    title="Chỉ số Q3"
                    headStyle={{ backgroundColor: '#0051A9', color: '#FFF', minHeight: '36px' }}>
                    <Form.Item validateStatus={inputError ? 'error' : ''} help={inputError || ''}>
                      <Input
                        placeholder={'Nhập chỉ số Q3'}
                        type="number"
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value && !Number.isInteger(Number(value))) {
                            setInputError('Vui lòng chỉ nhập vào số nguyên!');
                          } else {
                            setInputError('');
                          }

                          setCmd((state) => ({
                            ...state,
                            setQ3: { setQ3: e.target.value }
                          }));
                        }}
                      />
                    </Form.Item>
                    <Button
                      onClick={() => handleSendCmd('setQ3')}
                      disabled={inputError}
                      icon={<SendOutlined />}
                      style={{
                        backgroundColor: '#0051A9',
                        borderColor: '#0051A9',
                        color: '#FFF',
                        height: '36px'
                      }}>
                      Gửi lệnh
                    </Button>
                  </Card>
                </Col>
                <Col md={12} xs={24}>
                  <Card
                    title=" Chỉ số cực đại (L)"
                    headStyle={{ backgroundColor: '#0051A9', color: '#FFF', minHeight: '36px' }}>
                    <Form.Item>
                      <Select
                        style={{ width: '100%', height: '36px' }}
                        placeholder={'Select'}
                        onChange={(value) =>
                          setCmd((state) => ({
                            ...state,
                            setMaxReading: { setMaxReading: value }
                          }))
                        }
                        options={[
                          { value: '999999', label: '999,999' },
                          { value: '9999999', label: '9,999,999' },
                          { value: '99999999', label: '99,999,999' },
                          { value: '999999999', label: '999,999,999' },
                          { value: '9999999999', label: '9,999,999,999' },
                          { value: '99999999999', label: '99,999,999,999' },
                          { value: '999999999999', label: '999,999,999,999' }
                        ]}
                      />
                    </Form.Item>
                    <Button
                      onClick={() => handleSendCmd('setMaxReading')}
                      icon={<SendOutlined />}
                      style={{
                        backgroundColor: '#0051A9',
                        borderColor: '#0051A9',
                        color: '#FFF',
                        height: '36px'
                      }}>
                      Gửi lệnh
                    </Button>
                  </Card>
                </Col>
              </>
            )}
          </>
        )}

        {command?.viewHistory && (
          <>
            <Col span={24}>
              <Divider
                style={{
                  borderColor: '#0051A9',
                  borderWidth: '1px',
                  color: '#0051A9',
                  transform: undefined,
                  marginBottom: 32,
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                Lịch sử truyền lệnh
              </Divider>
              <Row
                justify="space-between"
                align="middle"
                style={{ marginBottom: '16px' }}
                gutter={16}>
                <Col md={7} xs={24}>
                  <Input
                    style={{ height: '36px', width: '100%' }}
                    placeholder="Tìm kiếm theo số serial, imei..."
                    value={search}
                    onChange={handleSearchChange}
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                </Col>
                <Col md={7} xs={24}>
                  <RangePicker
                    value={dateRange}
                    onChange={onChangeDateRange}
                    style={{ width: '100%', height: '36px', fontSize: '18px' }}
                    format="YYYY-MM-DD"
                    placeholder={['Từ ngày', 'Đến ngày']}
                  />
                </Col>
                <Col flex="auto" style={{ textAlign: 'right' }}>
                  <Space>
                    {command.exportFull && (
                      <>
                        <Button
                          icon={<DownloadOutlined />}
                          onClick={handleExportExcelButtonClick}
                          loading={loading}
                          style={{
                            color: '#fff',
                            height: '36px',
                            backgroundColor: '#0051A9',
                            borderColor: '#0051A9',
                            fontSize: '18px'
                          }}>
                          Xuất file excel
                        </Button>
                      </>
                    )}

                    {command?.import && (
                      <Button
                        icon={<UploadOutlined />}
                        onClick={() => setVisibleModalImport(true)}
                        style={{
                          color: '#fff',
                          height: '36px',
                          backgroundColor: '#0051A9',
                          borderColor: '#0051A9',
                          fontSize: '18px'
                        }}>
                        Nhập file excel
                      </Button>
                    )}
                  </Space>
                </Col>
              </Row>
              {/* <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
              <Col md={6} xs={12}>
                <Input
                  style={{ height: '36px', width: '100%', fontSize: '18px' }}
                  size="small"
                  placeholder="Tìm kiếm theo số serial, imei..."
                  value={search}
                  onChange={handleSearchChange}
                />
              </Col>
              <Col md={6} sm={12}></Col>
              <Col md={3} xs={6}>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={() => {
                    setCurrentPage(1);
                    setFlagSearch(!flagSearch);
                  }}
                  style={{
                    height: '36px',
                    backgroundColor: '#0051A9',
                    borderColor: '#0051A9',
                    fontSize: '18px'
                  }}>
                  Tìm kiếm
                </Button>
              </Col>
            </Row> */}
              <Table
                bordered
                // size="middle"
                dataSource={historicalCmd || []}
                columns={columns}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: totalItems,
                  showSizeChanger: true,
                  onChange: handleChangePage,
                  onShowSizeChange: handleChangePageSize,
                  showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
                }}
                style={{
                  borderRadius: 0,
                  thead: { backgroundColor: '#DBDFE5', color: '#626D7C' }
                }}
                scroll={{ x: 'max-content' }}
              />
            </Col>
          </>
        )}
      </Row>
    </div>
  );
}
